import { atom } from 'jotai'

import { api } from '../lib/api'
import { sessionAtom } from './session'

export type Profile = {
  id: string
  userId: string
  name: string
  bio?: string
  birthday?: string
  gender?: string
  sexuality?: string
  location?: string
  mainPhotoId?: string
  photoIds: string[]
  approvedPhotoIds: string[]
  isActive: boolean
  isVisible: boolean
  createdAt: string
  updatedAt: string
}

export type AttributeChip = {
  key: string
  text: string
}

export type AttributeCompletion = {
  complete: string[]
  partial: string[]
  missing: string[]
}

export type ProfileAttributes = {
  chips?: AttributeChip[]
  completion?: AttributeCompletion
}

export const profileAtom = atom<Profile | null>(null)

export const profileIsLoadingAtom = atom(false)

export const profileErrorAtom = atom<string | null>(null)

export const profileAttributesAtom = atom<ProfileAttributes | null>(null)

export const profileAttributesIsLoadingAtom = atom(false)

let attributesRequestSeq = 0

export const fetchProfileAtom = atom(
  null,
  async function fetchProfile(_get, set, profileId: string) {
    set(profileIsLoadingAtom, true)
    set(profileErrorAtom, null)

    try {
      const response = await api.fetch(`/profile/${profileId}`)

      if (!response.ok) {
        set(profileErrorAtom, 'Failed to fetch profile')
        set(profileIsLoadingAtom, false)
        return null
      }

      const profile = (await response.json()) as Profile
      set(profileAtom, profile)
      set(profileIsLoadingAtom, false)
      return profile
    } catch (_err) {
      set(profileErrorAtom, 'Network error')
      set(profileIsLoadingAtom, false)
      return null
    }
  },
)

export const fetchMyProfileAtom = atom(null, async function fetchMyProfile(get, set) {
  const session = get(sessionAtom)
  if (!session) {
    set(profileErrorAtom, 'Not authenticated')
    return null
  }

  set(profileIsLoadingAtom, true)
  set(profileErrorAtom, null)

  try {
    const response = await api.fetch(`/user/${session.user.id}/profile`)

    if (!response.ok) {
      set(profileErrorAtom, 'Failed to fetch profile')
      set(profileIsLoadingAtom, false)
      return null
    }

    const profile = (await response.json()) as Profile
    set(profileAtom, profile)
    set(profileIsLoadingAtom, false)
    return profile
  } catch (_err) {
    set(profileErrorAtom, 'Network error')
    set(profileIsLoadingAtom, false)
    return null
  }
})

export const fetchProfileAttributesAtom = atom(
  null,
  async function fetchProfileAttributes(_get, set, profileId: string) {
    const requestId = (attributesRequestSeq += 1)
    set(profileAttributesIsLoadingAtom, true)

    try {
      const response = await api.fetch(`/profile/${profileId}/attributes`)
      if (requestId !== attributesRequestSeq) return null

      if (!response.ok) {
        set(profileAttributesIsLoadingAtom, false)
        return null
      }

      const attributes = (await response.json()) as ProfileAttributes
      set(profileAttributesAtom, attributes)
      set(profileAttributesIsLoadingAtom, false)
      return attributes
    } catch (_err) {
      if (requestId !== attributesRequestSeq) return null
      set(profileAttributesIsLoadingAtom, false)
      return null
    }
  },
)

export const clearProfileAtom = atom(null, function clearProfile(_get, set) {
  set(profileAtom, null)
  set(profileErrorAtom, null)
  set(profileAttributesAtom, null)
})

export type ProfileUpdate = {
  name?: string
  bio?: string
  birthday?: string
  gender?: string
  sexuality?: string
  location?: string
  isVisible?: boolean
}

export const updateProfileAtom = atom(
  null,
  async function updateProfile(get, set, updates: ProfileUpdate) {
    const currentProfile = get(profileAtom)
    const session = get(sessionAtom)
    const profileId = currentProfile?.id ?? session?.user.profile?.id
    if (!profileId) {
      set(profileErrorAtom, 'No profile')
      return { success: false as const, error: 'noProfile' }
    }

    set(profileIsLoadingAtom, true)
    set(profileErrorAtom, null)

    try {
      const response = await api.fetch(`/profile/${profileId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      })

      if (!response.ok) {
        set(profileErrorAtom, 'Failed to update profile')
        set(profileIsLoadingAtom, false)
        return { success: false as const, error: 'updateFailed' }
      }

      const updatedProfile = (await response.json()) as Profile
      set(profileAtom, updatedProfile)

      // Refresh session so own-profile views stay in sync
      const sessionResponse = await api.fetch('/tokens', { method: 'GET' })
      if (sessionResponse.ok) {
        const sessionData = await sessionResponse.json()
        set(sessionAtom, sessionData)
      }

      set(profileIsLoadingAtom, false)
      return { success: true as const, profile: updatedProfile }
    } catch {
      set(profileErrorAtom, 'Network error')
      set(profileIsLoadingAtom, false)
      return { success: false as const, error: 'networkError' }
    }
  },
)

export const setMainPhotoAtom = atom(null, async function setMainPhoto(get, set, photoId: string) {
  const currentProfile = get(profileAtom)
  if (!currentProfile) return { success: false, error: 'noProfile' }

  try {
    const response = await api.fetch(`/profile/${currentProfile.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ mainPhotoId: photoId }),
    })

    if (!response.ok) {
      const errorData = await response.json()
      return { success: false, error: errorData }
    }

    const updatedProfile = (await response.json()) as Profile
    set(profileAtom, updatedProfile)

    // Update session to refresh user profile with new mainPhotoId
    const session = get(sessionAtom)
    if (session) {
      const sessionResponse = await api.fetch('/tokens', { method: 'GET' })
      if (sessionResponse.ok) {
        const sessionData = await sessionResponse.json()
        set(sessionAtom, sessionData)
      }
    }

    return { success: true }
  } catch (_error) {
    return { success: false, error: 'networkError' }
  }
})
